<script setup>
import { useAbility } from '@casl/vue'
import { VDataTable } from 'vuetify/lib/labs/VDataTable/index.mjs'

import { cookieRef } from '@/@layouts/stores/config'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import RiskService from '@/services/quality/risk.service'
import { registerLevelTitle } from '@/enums/quality/register-level'
import { cadenceModeTitle } from '@/enums/quality/cadence-mode'
import { reviewFrequencyTitle } from '@/enums/quality/review-frequency'
import { scoringMethodTitle } from '@/enums/quality/scoring-method'
import { requirementLevelColor, requirementLevelIsScorable, requirementLevelTitle } from '@/enums/quality/requirement-level'
import StandardRefService from '@/services/quality/standardRef.service'

// unplugin-vue-router derives the route name from the file path
// (`app-quality-risk-masters-types`) — see src/pages/app/quality/risk/[id].vue
// for the same convention (meta.name is a CASL key, not a route name, so it
// is intentionally absent here).
definePage({
  meta: {
    subject: 'risk.menu',
    action: 'masters',
  },
})

const { can } = useAbility()
const unitId = cookieRef('selectedUnit')
const router = useRouter()

// ── List ─────────────────────────────────────────────
// RiskTypeController::index has no pagination, search or sort params — it
// always returns the full active-or-not collection for the caller's unit
// (Modules/Quality/App/Http/Controllers/Risk/RiskTypeController.php). A
// VDataTableServer bound to page/per-page would therefore be decorative: the
// backend ignores those params and returns everything every time, so paging
// controls would do nothing. Plain VDataTable with its built-in client-side
// `search` prop matches what the endpoint actually provides.
const searchQuery = ref('')
const riskTypes = ref([])
const isFetching = ref(false)

// Monotonic token guarding against out-of-order responses — same pattern as
// src/pages/app/quality/risk/index.vue.
let fetchToken = 0

const fetchTypes = async () => {
  const token = ++fetchToken

  isFetching.value = true

  // unit_id matters: RiskTypeController::index only filters when it is sent, and
  // UnitScope spans every unit the caller belongs to — so a multi-unit user saw
  // N x 17 rows with duplicate codes and no column to tell them apart, then
  // edited whichever unit's row they happened to click.
  await RiskService.listTypes({ unit_id: unitId.value }, data => {
    if (token !== fetchToken) return

    // Newest first. The endpoint returns the collection in code order, which is
    // alphabetical and therefore arbitrary to a reader — a type added today
    // landed in the middle of the list with nothing to draw the eye to it.
    // Sorted on `id` rather than `created_at` because RiskTypeResource does not
    // expose a timestamp; the column is auto-increment, so descending id is an
    // exact proxy for insertion order. Sorting the array (rather than binding
    // `sort-by`) leaves the table's own column sorting free, and clicking a
    // header off again falls back to this order.
    riskTypes.value = [...(data?.data ?? data ?? [])].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
    isFetching.value = false
  }, () => {
    if (token !== fetchToken) return
    isFetching.value = false
  })
}

fetchTypes()

const headers = [
  { title: 'Code', key: 'code' },
  { title: 'Name', key: 'name' },
  { title: 'Register Level', key: 'register_level_default' },
  { title: 'Cadence Mode', key: 'cadence_mode' },
  { title: 'Mandated Frequency', key: 'mandated_frequency' },
  { title: 'Scoring Method', key: 'default_scoring_method' },
  { title: 'Standard Refs', key: 'standard_refs', sortable: false },
  { title: 'Requirement', key: 'requirement', sortable: false },
  { title: 'Active', key: 'is_active', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false, width: 100 },
]

// ── Setup page navigation ───────────────────────────
const openCreate = () => router.push({ name: 'app-quality-risk-masters-types-setup' })
const openEdit = item => router.push({ name: 'app-quality-risk-masters-types-setup', params: { id: item.id } })

// ── Active toggle ────────────────────────────────────
// PUT is a full RiskTypeRequest validation, not a partial patch — every
// required field must be resent, not just is_active (see
// Modules/Quality/App/Http/Requests/Risk/RiskTypeRequest.php).
const togglingId = ref(null)

// ── Requirement level ────────────────────────────────
// Activating a type puts it into this unit's assessment-compliance denominator
// (RiskAssessmentComplianceService counts active FIXED types with a mandated
// frequency), so activating one whose references are only guidance would
// manufacture a real overdue figure out of an aspiration.
const standardRefs = ref([])

const fetchStandardRefs = async () => {
  await StandardRefService.list({}, data => {
    standardRefs.value = data?.data ?? data ?? []
  })
}

fetchStandardRefs()

const refsByCode = computed(() => Object.fromEntries(standardRefs.value.map(r => [r.code, r])))

const requirementLevels = type => [...new Set((type.standard_refs ?? [])
  .map(code => refsByCode.value[code]?.requirement_level)
  .filter(Boolean))]

// Uses the shared scorable rule rather than testing for INTENT alone: the
// catalogue has three non-scorable levels, and a type referenced only by
// Guidebook interpretations would otherwise slip through as mandated.
const isIntentOnly = type => {
  const levels = requirementLevels(type)

  return levels.length > 0 && levels.every(l => !requirementLevelIsScorable(l))
}

const confirmIntentActivation = ref(false)
const pendingActivation = ref(null)

const onToggleActive = item => {
  if (!item.is_active && isIntentOnly(item)) {
    pendingActivation.value = item
    confirmIntentActivation.value = true

    return
  }

  toggleActive(item)
}

// ConfirmDialog emits confirm(false) from its Cancel button, so the flag must
// be honoured — ignoring it would activate the type the dialog exists to guard.
const onConfirmIntentActivation = confirmed => {
  if (confirmed && pendingActivation.value) toggleActive(pendingActivation.value)
  pendingActivation.value = null
}

const toggleActive = async item => {
  togglingId.value = item.id

  const payload = {
    code: item.code,
    name: item.name,
    description: item.description,
    icon: item.icon,
    register_level_default: item.register_level_default,
    cadence_mode: item.cadence_mode,
    mandated_frequency: item.mandated_frequency,
    default_scoring_method: item.default_scoring_method,
    standard_refs: item.standard_refs,
    is_active: !item.is_active,
    unit_id: unitId.value,
  }

  await RiskService.updateType(item.id, payload, () => {
    togglingId.value = null
    fetchTypes()
  }, () => {
    togglingId.value = null
    fetchTypes() // resync the switch to server truth on failure
  })
}

// ── Delete ───────────────────────────────────────────
// System types cannot be deleted server-side (RiskTypeController::destroy
// aborts with 422) — the disabled button + tooltip below reflect that rather
// than offering an action that always fails.
const isConfirmDialogVisible = ref(false)
const deleteTarget = ref(null)

const showConfirmDialog = item => {
  deleteTarget.value = item
  isConfirmDialogVisible.value = true
}

const confirmDelete = async (confirm, data) => {
  if (confirm && data?.id) {
    await RiskService.deleteType(data.id, () => {
      isConfirmDialogVisible.value = false
      fetchTypes()
    })
  }
}
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardItem>
        <template #title>
          <AppTextField
            v-model="searchQuery"
            placeholder="Search..."
            density="compact"
            style="inline-size: 280px;"
          >
            <template #prepend-inner>
              <VIcon
                icon="tabler-search"
                size="16"
              />
            </template>
          </AppTextField>
        </template>
        <template #append>
          <div class="d-flex align-center gap-3">
            <VBtn
              v-if="can('configure', 'quality.risk')"
              color="primary"
              variant="flat"
              prepend-icon="tabler-plus"
              @click="openCreate"
            >
              Add Risk Type
            </VBtn>

            <IconBtn
              variant="tonal"
              class="rounded"
              :loading="isFetching"
              @click="fetchTypes"
            >
              <VIcon icon="tabler-refresh" />
              <VTooltip activator="parent">
                Refresh
              </VTooltip>
            </IconBtn>
          </div>
        </template>
      </VCardItem>

      <VDivider />

      <VDataTable
        :headers="headers"
        :items="riskTypes"
        :search="searchQuery"
        :loading="isFetching"
        class="text-no-wrap"
      >
        <!-- Code -->
        <template #item.code="{ item }">
          <div class="d-flex align-center gap-1">
            <VIcon
              v-if="item.is_system"
              icon="tabler-lock"
              size="14"
              class="text-medium-emphasis"
            />
            <span class="font-weight-medium">{{ item.code }}</span>
          </div>
        </template>

        <!-- Register Level -->
        <template #item.register_level_default="{ item }">
          {{ registerLevelTitle(item.register_level_default) }}
        </template>

        <!-- Cadence Mode -->
        <template #item.cadence_mode="{ item }">
          {{ cadenceModeTitle(item.cadence_mode) }}
        </template>

        <!-- Mandated Frequency -->
        <template #item.mandated_frequency="{ item }">
          <span v-if="item.mandated_frequency">{{ reviewFrequencyTitle(item.mandated_frequency) }}</span>
          <span
            v-else
            class="text-disabled"
          >—</span>
        </template>

        <!-- Scoring Method -->
        <template #item.default_scoring_method="{ item }">
          {{ scoringMethodTitle(item.default_scoring_method) }}
        </template>

        <!-- Standard Refs -->
        <template #item.standard_refs="{ item }">
          <div
            v-if="item.standard_refs?.length"
            class="d-flex flex-wrap gap-1"
          >
            <VChip
              v-for="ref in item.standard_refs"
              :key="ref"
              size="x-small"
              label
            >
              {{ ref }}
            </VChip>
          </div>
          <span
            v-else
            class="text-disabled"
          >—</span>
        </template>

        <!-- Requirement level -->
        <template #item.requirement="{ item }">
          <div
            v-if="requirementLevels(item).length"
            class="d-flex flex-wrap align-center gap-1"
          >
            <VChip
              v-for="level in requirementLevels(item)"
              :key="level"
              size="x-small"
              label
              :color="requirementLevelColor(level)"
            >
              {{ requirementLevelTitle(level) }}
            </VChip>
            <VChip
              v-if="isIntentOnly(item)"
              size="x-small"
              label
              color="warning"
            >
              <template #prepend>
                <VIcon
                  icon="tabler-info-circle"
                  size="16"
                />
              </template>
              <span class="ms-1">Recommended, not mandated</span>
            </VChip>
          </div>
          <span
            v-else
            class="text-disabled"
          >—</span>
        </template>

        <!-- Active -->
        <template #item.is_active="{ item }">
          <VSwitch
            :model-value="item.is_active"
            :disabled="!can('configure', 'quality.risk') || togglingId === item.id"
            color="success"
            density="compact"
            hide-details
            @update:model-value="onToggleActive(item)"
          />
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <div
            v-if="can('configure', 'quality.risk')"
            class="d-flex justify-start gap-1"
          >
            <IconBtn
              variant="tonal"
              class="rounded"
              size="small"
              @click="openEdit(item)"
            >
              <VIcon icon="tabler-edit" />
              <VTooltip activator="parent">
                Edit
              </VTooltip>
            </IconBtn>

            <IconBtn
              v-if="!item.is_system"
              variant="tonal"
              class="rounded"
              size="small"
              color="error"
              @click="showConfirmDialog(item)"
            >
              <VIcon icon="tabler-trash" />
              <VTooltip activator="parent">
                Delete
              </VTooltip>
            </IconBtn>

            <IconBtn
              v-else
              variant="tonal"
              class="rounded"
              size="small"
              disabled
            >
              <VIcon icon="tabler-lock" />
              <VTooltip activator="parent">
                System types can be deactivated, not deleted.
              </VTooltip>
            </IconBtn>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure you want to delete this risk type?"
      confirm-title="Deleted!"
      confirm-msg="Risk type deleted."
      :data="deleteTarget"
      @confirm="confirmDelete"
    />

    <!--
      The whole warning lives in confirmation-question: ConfirmDialog renders
      confirmTitle and confirmationQuestion only, so text placed in
      confirm-msg would never be seen. 
    -->
    <ConfirmDialog
      v-model:isDialogVisible="confirmIntentActivation"
      confirmation-question="This type's standard references are guidance, not mandated measurable elements. Activating it adds it to this unit's assessment compliance denominator, so it will start showing as overdue. Activate anyway?"
      confirm-title="Activated"
      confirm-msg="Assessment type activated."
      @confirm="onConfirmIntentActivation"
    />
  </section>
</template>

<!--
  The module's standard data-table footer treatment — identical to
  quality/master/type.vue, quality/rca/index.vue and the rest. Deliberately
  unscoped: the footer is rendered by VDataTable, and `margin-block-start: 0`
  has to beat the global `.v-data-table-footer { margin-block-start: 1rem }` in
  @core/scss/base/libs/vuetify/_overrides.scss, which is what otherwise leaves
  the footer floating detached below the last row.
-->
<style>
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
